import React, { useState } from 'react';
import OverflowMenu from '../../components/OverflowMenu';
import { MenuItem, MenuSeparator } from '../../components/Menu';
import useDeleteReactionThread from './useDeleteReactionThread';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import { UIActions } from '../../ui/uiSlice';
import reactionThreadHash from '../../utils/url/reactionThreadHash';
import fullOrgUrl from '../../utils/url/fullOrgUrl';
import postPath from '../../utils/url/postPath';
import type { DocumentType } from '../../graphql/generated';
import { gql } from '../../graphql/generated';
import trashIcon from '../../icons/non-prefilled/16x16/trash.svg';
import linkIcon from '../../icons/non-prefilled/16x16/link.svg';

const POST_FRAGMENT = gql(`
  fragment ReactionThreadMenu_post on RootPostFields {
    id
    ...postPath_post
  }
`);

const THREAD_FRAGMENT = gql(`
  fragment ReactionThreadMenu_thread on PublicThreadFields {
    id
    author {
      id
    }
  }
`);

interface ReactionThreadMenuProps {
  post: DocumentType<typeof POST_FRAGMENT>;
  thread: DocumentType<typeof THREAD_FRAGMENT>;
  button: React.ReactNode;
}

const ReactionThreadMenu = ({
  thread,
  post,
  button,
}: ReactionThreadMenuProps) => {
  const dispatch = useAppDispatch();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const deleteReactionThread = useDeleteReactionThread();
  const currentUser = useAppSelector((state) => state.session.user);
  const canDeleteReactionThread = thread.author?.id === currentUser?.id;

  const handleDeleteAllReactions = () => {
    deleteReactionThread(thread.id);
  };

  const onCopyLink = () => {
    const url = fullOrgUrl(`${postPath(post)}${reactionThreadHash(thread.id)}`);

    navigator.clipboard.writeText(url).then(() => {
      dispatch(
        UIActions.pushToast({
          level: 'success',
          message: 'Link copied to clipboard',
        }),
      );
    });
  };

  return (
    <OverflowMenu
      inline={false}
      open={isMenuOpen}
      onOpenChange={setIsMenuOpen}
      button={button}
    >
      <MenuItem icon={linkIcon} onClick={onCopyLink}>
        Copy link to reactions
      </MenuItem>
      {canDeleteReactionThread && (
        <>
          <MenuSeparator />
          <MenuItem icon={trashIcon} danger onClick={handleDeleteAllReactions}>
            Delete this thread
          </MenuItem>
        </>
      )}
    </OverflowMenu>
  );
};

export default ReactionThreadMenu;
